import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { DollarSign, Briefcase, TrendingUp, Calendar, Award, AlertCircle } from 'lucide-react';

interface GuideTabProps {
  employmentType: 'commission' | 'salary';
}

export default function GuideTab({ employmentType }: GuideTabProps) {
  return (
    <div className="space-y-6">
      {/* Employment Type Badge */}
      <Card className="shadow-sm border-2">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg mb-2">Your Employment Type</h3>
              <p className="text-sm text-gray-600">This determines how you earn from sales</p>
            </div>
            <Badge 
              variant={employmentType === 'commission' ? 'default' : 'outline'}
              className="text-lg px-4 py-2"
            >
              {employmentType === 'commission' ? (
                <><DollarSign className="w-5 h-5 mr-2" /> Commission-Based</>
              ) : (
                <><Briefcase className="w-5 h-5 mr-2" /> Salary-Based</>
              )}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* How It Works */}
      {employmentType === 'commission' ? (
        <CommissionGuide />
      ) : (
        <SalaryGuide />
      )}

      {/* Common Steps */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            How to Sell Plans
          </CardTitle>
          <CardDescription>Follow these steps to maximize your sales</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <StepCard
              number={1}
              title="Create Coupon Codes"
              description="Go to Coupons tab and create discount codes for different plans. Set attractive discounts (10-30%) to encourage purchases."
            />
            <StepCard
              number={2}
              title="Share Your Coupons"
              description="Share your coupon codes with potential customers through WhatsApp, social media, or direct messaging."
            />
            <StepCard
              number={3}
              title="Customer Makes Purchase"
              description="When customers use your coupon code to buy a plan, the sale is tracked to your account."
            />
            <StepCard
              number={4}
              title={employmentType === 'commission' ? "Earn Commission" : "Track Performance"}
              description={
                employmentType === 'commission'
                  ? "You automatically earn commission (50% default) on each successful sale. Commission is added to your pending earnings."
                  : "Your sales are tracked in the system for performance review. Admin can see your total sales and revenue generated."
              }
            />
            {employmentType === 'commission' && (
              <StepCard
                number={5}
                title="Request Payout"
                description="Once you have pending earnings, request a payout. Admin will review and transfer money to your registered bank account."
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Important Notes */}
      <Card className="shadow-sm border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-900">
            <AlertCircle className="w-5 h-5" />
            Important Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-amber-900">
          <p>• Only purchases made through YOUR coupon codes will be tracked to your account</p>
          <p>• Customers must complete the payment for the sale to be valid</p>
          <p>• Refunded orders will reverse any commission or sales count</p>
          {employmentType === 'commission' ? (
            <>
              <p>• Commission percentage may be changed by admin at any time</p>
              <p>• Minimum payout amount and payout processing time may vary</p>
            </>
          ) : (
            <>
              <p>• As a salary-based employee, you receive fixed monthly salary</p>
              <p>• Focus on sales volume as performance is tracked for reviews</p>
            </>
          )}
          <p>• Keep your bank details updated in Account Settings</p>
          <p>• Contact admin if you have any questions or issues</p>
        </CardContent>
      </Card>
    </div>
  );
}

function CommissionGuide() {
  return (
    <Card className="shadow-sm bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-green-900">
          <DollarSign className="w-5 h-5" />
          Commission-Based Employment
        </CardTitle>
        <CardDescription className="text-green-700">
          Earn a percentage on every plan sold through your coupons
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <MetricCard
            icon={<Award className="w-6 h-6 text-green-600" />}
            label="Commission Rate"
            value="50%"
            description="Default commission on each sale"
          />
          <MetricCard
            icon={<TrendingUp className="w-6 h-6 text-green-600" />}
            label="Earnings Type"
            value="Variable"
            description="Based on your sales performance"
          />
          <MetricCard
            icon={<Calendar className="w-6 h-6 text-green-600" />}
            label="Payout"
            value="On Request"
            description="Request when you have pending earnings"
          />
        </div>

        <div className="bg-white rounded-lg p-4 space-y-3">
          <h4 className="text-sm">How Commission Works:</h4>
          <div className="space-y-2 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5" />
              <div>
                <span>Customer buys ₹10,000 plan using your coupon with 20% discount</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5" />
              <div>
                <span>Final amount after discount: ₹8,000</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5" />
              <div>
                <span>Your commission (50% of ₹8,000): <strong>₹4,000</strong></span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5" />
              <div>
                <span>Commission added to pending earnings</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5" />
              <div>
                <span>Request payout when ready, admin processes and transfers to your bank</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-green-100 rounded-lg p-4">
          <p className="text-sm text-green-900">
            <strong>Pro Tip:</strong> Higher sales volume = Higher earnings! Focus on creating attractive discounts and promoting your coupons actively.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function SalaryGuide() {
  return (
    <Card className="shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Briefcase className="w-5 h-5" />
          Salary-Based Employment
        </CardTitle>
        <CardDescription className="text-blue-700">
          Fixed monthly salary with performance tracking
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <MetricCard
            icon={<Briefcase className="w-6 h-6 text-blue-600" />}
            label="Salary Type"
            value="Fixed"
            description="Receive regular monthly salary"
          />
          <MetricCard
            icon={<TrendingUp className="w-6 h-6 text-blue-600" />}
            label="Performance"
            value="Tracked"
            description="Sales monitored for reviews"
          />
          <MetricCard
            icon={<Calendar className="w-6 h-6 text-blue-600" />}
            label="Payment"
            value="Monthly"
            description="Handled externally by admin"
          />
        </div>

        <div className="bg-white rounded-lg p-4 space-y-3">
          <h4 className="text-sm">How Salary Employment Works:</h4>
          <div className="space-y-2 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5" />
              <div>
                <span>You receive a fixed monthly salary regardless of sales</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5" />
              <div>
                <span>Every sale you make is tracked in the system</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5" />
              <div>
                <span>Admin monitors total plans sold and revenue generated</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5" />
              <div>
                <span>Performance reviewed for potential salary increases or bonuses</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5" />
              <div>
                <span>No commission earned, but job stability with regular income</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4">
          <h4 className="text-sm mb-2">Example Scenario:</h4>
          <div className="space-y-2 text-sm text-gray-700">
            <p>• Month: November 2025</p>
            <p>• Plans Sold: 25 plans</p>
            <p>• Total Revenue Generated: ₹2,50,000</p>
            <p>• Commission: ₹0 (Salary-based employee)</p>
            <p>• Your Salary: ₹20,000 (paid externally by admin)</p>
          </div>
        </div>

        <div className="bg-blue-100 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Pro Tip:</strong> Even though you don{`'`}t earn commission, high performance can lead to salary increments, bonuses, and career growth opportunities!
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

interface StepCardProps {
  number: number;
  title: string;
  description: string;
}

function StepCard({ number, title, description }: StepCardProps) {
  return (
    <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center">
        {number}
      </div>
      <div>
        <h4 className="mb-1">{title}</h4>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  description: string;
}

function MetricCard({ icon, label, value, description }: MetricCardProps) {
  return (
    <div className="bg-white rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <div className="mb-1">{value}</div>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  );
}
